// app/javascript/controllers/address_autocomplete_controller.js
import { Controller } from "@hotwired/stimulus";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export default class extends Controller {
  static targets = [
    "container",
    "landmark",
    "street",
    "city",
    "state",
    "postal",
    "country",
  ];
  static values = { apiKey: String };

  async connect() {
    setOptions({ key: this.apiKeyValue, version: "weekly" });
    const { PlaceAutocompleteElement } = await importLibrary("places");
    const { Geocoder } = await importLibrary("geocoding"); // Load Geocoder for fallback

    this.geocoder = new Geocoder();
    this.autocompleteElement = new PlaceAutocompleteElement();
    this.containerTarget.appendChild(this.autocompleteElement);

    this.autocompleteElement.addEventListener(
      "gmp-select",
      async ({ placePrediction }) => {
        const place = placePrediction.toPlace();

        // Fetch core fields
        await place.fetchFields({
          fields: ["addressComponents", "displayName", "location"],
        });

        // If postal code or state is missing, use Reverse Geocoding
        if (!this.hasRequiredComponents(place)) {
          await this.fallbackToGeocoding(place);
        } else {
          this.fillInAddress(place.addressComponents, place.displayName);
        }
      }
    );
  }

  hasRequiredComponents(place) {
    const types = place.addressComponents?.flatMap((c) => c.types) || [];
    return (
      types.includes("postal_code") &&
      types.includes("administrative_area_level_1")
    );
  }

  async fallbackToGeocoding(place) {
    const response = await this.geocoder.geocode({ location: place.location });
    if (response.results[0]) {
      // The Geocoder returns legacy address_components format
      this.fillInAddressLegacy(
        response.results[0].address_components,
        place.displayName
      );
    }
  }

  fillInAddress(components, displayName) {
    const mapping = {
      street_number: "",
      route: "",
      locality: "",
      administrative_area_level_1: "",
      postal_code: "",
      country: "",
    };

    components.forEach((c) => {
      c.types.forEach((type) => {
        if (type in mapping) mapping[type] = c.longText;
      });
    });

    this.updateForm(mapping, displayName);
  }

  // Helper for the Geocoder result (legacy property names)
  fillInAddressLegacy(components, displayName) {
    const mapping = {
      street_number: "",
      route: "",
      locality: "",
      administrative_area_level_1: "",
      postal_code: "",
      country: "",
    };

    components.forEach((c) => {
      c.types.forEach((type) => {
        if (type in mapping) mapping[type] = c.long_name;
      });
    });

    this.updateForm(mapping, displayName);
  }

  updateForm(mapping, displayName) {
    if (this.hasLandmarkTarget) this.landmarkTarget.value = displayName || "";
    if (this.hasStreetTarget)
      this.streetTarget.value =
        `${mapping.street_number} ${mapping.route}`.trim();
    if (this.hasCityTarget) this.cityTarget.value = mapping.locality;
    if (this.hasStateTarget)
      this.stateTarget.value = mapping.administrative_area_level_1;
    if (this.hasPostalTarget) this.postalTarget.value = mapping.postal_code;
    if (this.hasCountryTarget) this.countryTarget.value = mapping.country;
  }
}
